(ns frontend.view.settings.sys-admin.tenant
  (:require
   [clojure.data :as data]
   [common.email :as email]
   [common.localization :as loc]
   [common.schema.constraints :as constraints]
   [common.schema.entities.login-page-config :as login-page-config-schema]
   [common.schema.entities.lp-portal.lp-report :as lp-report]
   [common.schema.entities.lp-portal.lp-report-holding :as lp-report-holding]
   [common.schema.registry :as registry]
   [common.themes.registry :as theme-registry]
   [common.util :as cu]
   [frontend.actions.entity :as entity-actions]
   [frontend.conn :as conn]
   [frontend.state.current-session :as session]
   [frontend.subscription :as subscription]
   [frontend.utils :as fu]
   [frontend.view.components.form.view :as form]
   [frontend.view.modals.confirmation.view :as confirmation]
   [frontend.view.modals.events :as modals]
   [frontend.view.settings.components.image-uploader :as image-uploader]
   [frontend.view.settings.sys-admin.components.feature :as feature]
   [frontend.view.settings.sys-admin.db :as sys-admin-db]
   [frontend.view.settings.sys-admin.event :as event]
   [frontend.view.settings.sys-admin.events :as events]
   [frontend.view.settings.sys-admin.tenant.sessions.view :as tenant-sessions]
   [frontend.view.settings.sys-admin.tenant.users.view :as tenant-users]
   [frontend.view.settings.sys-admin.zadmin.analytics.view :as zadmin-analytics]
   [potpuri.core :as p]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.alert.view :as alert]
   [ui.components.button.view :as button]
   [ui.components.toggle.view :as toggle]
   [ui.editors.data-input :as data-input]
   [ui.editors.labelled-input.view :as labelled-input]))

(defn status-field
  [tenant]
  [:div
   {:class "px-4 pb-3 flex items-center gap-2"}
   [:span (loc/tr :tenant-status-active)]
   [toggle/toggle
    {:checked?  (not (:common/disabled? tenant))
     :on-change (partial events/change-status tenant)}]])

(defn validated-text-field
  [_opts]
  (let [local (r/atom {:dirty? false :value nil})]
    (fn [{:keys [label value validate! save!]}]
      (let [current (if (:dirty? @local) (:value @local) value)
            error   (when (fn? validate!) (validate! current))]
        [:div
         [labelled-input/view
          {:data-type :data.type/text-simple
           :label     label
           :value     current
           :error     error
           :on-change (fn [new-value]
                        (reset! local {:dirty? true :value new-value}))
           :on-blur   (fn [_]
                        (let [v (if (:dirty? @local) (:value @local) value)]
                          (when (and (not (and (fn? validate!) (validate! v)))
                                     (not= v value))
                            (save! v)
                            (reset! local {:dirty? false :value nil}))))}]
         (when error
           [:p {:class "text-xs text-danger-light-text"} error])]))))

(defn text-field
  ([attribute tenant]
   (text-field attribute tenant get false))
  ([attribute tenant getter read-only?]
   (let [tenant-schema @session/tenant-schema]
     [:div
      {:class "px-4"}
      [form/view
       {:label        (let [fs (registry/get-field-schema tenant-schema attribute)]
                        (loc/field-name attribute fs))
        :type         "text"
        :value        (getter tenant attribute)
        :edit?        false
        :read-only?   read-only?
        :on-change!   (partial events/modify-tenant tenant attribute)
        :wrapper      "zap-form__light-grey__green__sand"
        :control-id   "stage-name"
        :on-save!     identity
        :cancel-edit! (partial events/cancel-tenant-modify attribute)}]])))

(defn tenant-image
  [tenant attribute label]
  (let [value (get tenant attribute)]
    [image-uploader/image-view
     {:edit?              (empty? value)
      :value              (get tenant attribute)
      :key                attribute
      :label              label
      :remove-button-text (loc/tr :remove)
      :on-remove!         (fn [_]
                            (events/remove-image! tenant attribute))
      :on-change          (fn [e]
                            (events/save-image! tenant attribute e))}]))

(defn tenant-images
  [tenant]
  [:div
   {:class "px-4"}
   [:div.zap-form__light-grey__green__sand.form__text.form-group
    [:label.zap-label (loc/tr :tenant-logo)]
    [tenant-image tenant :tenant/logo (loc/tr :company-settings-logo)]]])

(defn update-holding!
  [attribute value]
  (swap! sys-admin-db/new-holding assoc attribute value))

(defn input-field
  [attribute mandatory?]
  (let [new-holding (:new-holding @sys-admin-db/sys-admin)
        schema
        (case (namespace attribute)
          "asset" (p/deep-merge
                   (registry/get-namespace-schema @session/tenant-schema :entity.type/company)
                   {:asset/name
                    {:field/default-visibility {:editor :editor.type/text-simple}}})
          "company" (p/deep-merge
                     (registry/get-namespace-schema @session/tenant-schema :entity.type/company)
                     {:asset/name
                      {:field/default-visibility {:editor :editor.type/text-simple}}})
          "holding" (p/deep-merge
                     (registry/get-namespace-schema @session/tenant-schema :entity.type/holding)
                     {:holding/name
                      {:field/default-visibility {:editor :editor.type/text-simple}}}))
        field-schema (get schema attribute)
        value (get new-holding attribute)
        error (constraints/validate (:field/constraints field-schema)
                                    nil
                                    value
                                    new-holding
                                    (constantly nil)
                                    loc/tr)]
    [:div
     [labelled-input/view
      {:data-type  (:field/data-type field-schema)
       :label      (or (->> field-schema :field/translations :en)
                       (loc/tr attribute)
                       (loc/field-name attribute field-schema))
       :mandatory? mandatory?
       :value      value
       :error      error
       :on-change  (fn [new-value]
                     (update-holding! attribute new-value))}]
     (when error
       [:p {:class "text-xs text-danger-light-text"} error])]))

(defn new-holding
  [tenant]
  (let [new-holding (:new-holding @sys-admin-db/sys-admin)]
    [:div
     {:class "px-4"}
     (when (:processing new-holding)
       [:div
        (loc/tr :processing)])
     [input-field :asset/name true]
     [input-field :company/website false]
     [input-field :holding/name true]
     [input-field :holding/cur-paid-in-capital false]
     [:div
      {:class "flex gap-3 my-3"}
      [button/secondary-hollow
       {:on-click events/end-holding-creation!}
       [:span (loc/tr :cancel)]]
      [button/primary-solid
       {:on-click #(events/create-holding! tenant)}
       [:span (loc/tr :create)]]]]))

(defn holding-creation
  [tenant]
  (let [started? (boolean (:new-holding @sys-admin-db/sys-admin))
        info @sys-admin-db/new-holding-info]
    [:div
     {:class-name "mb-4 flex flex-col gap-4"}
     (when info
       [alert/info
        {:on-close events/hide-holding-info!}
        info])
     (if-not started?
       [:div
        [button/sm-primary-solid
         {:class-name "w-auto"
          :on-click   (fn [_]
                        (events/start-holding-creation!))}
         [:span (loc/tr :add-company-and-holding)]]]
       [:div
        {:class "border border-solid border-base-light-border-paler rounded"}
        [:div
         {:class "p-3"}
         [new-holding tenant]]])]))

(defn block-password-login
  [tenant]
  (let [attribute :tenant/block-password-login
        value (get tenant attribute)]
    [:div
     {:class "py-2 px-3 inline-flex items-center gap-3"}
     [:span "block password login"]
     [toggle/toggle
      {:checked?  value
       :on-change (fn [new-value]
                    (events/modify-tenant tenant
                                          attribute
                                          new-value))}]]))

(defn esign-v2
  [tenant]
  (let [attribute :tenant/esign-v2?
        value (get tenant attribute)]
    [:div
     {:class "py-2 px-3 inline-flex items-center gap-3"}
     [:span "Use E-sign v2 when creating new e-sign processes?"]
     [toggle/toggle
      {:checked?  value
       :on-change (fn [new-value]
                    (events/modify-tenant tenant
                                          attribute
                                          new-value))}]]))

(defn nev-2023-selector
  [tenant]
  (let [attribute :tenant/nev-2023
        tenant-schema @session/tenant-schema
        field-schema (registry/get-field-schema tenant-schema attribute)
        value (get tenant attribute)
        options (->> (:options field-schema)
                     (mapv (fn [{:keys [key value label]}]
                             {:value (or value key)
                              :label label})))]
    [:div
     {:class "px-4"}
     [labelled-input/view
      {:editor-type   :editor.type/select
       :data-type     :data.type/keyword
       :label         (loc/field-name attribute field-schema)
       :is-clearable? false
       :value         value
       :options       options
       :on-change     (fn [new-value & _]
                        (events/modify-tenant tenant attribute new-value (fn [_]) (fn [_])))}]]))

(defn configuration-view
  [tenant]
  [:div
   {:class "border border-solid border-base-light-border-paler rounded"}
   [:div
    {:class "p-3 flex justify-between items-center border-b border-solid border-base-light-border-paler"}
    [:h2
     {:class "font-bold"}
     (loc/tr :settings)]]
   [:div
    {:class "p-3"}
    [text-field :tenant/name tenant]
    [status-field tenant]
    [text-field :tenant.inbox/email tenant]
    [text-field :common/hubspot-id tenant]
    [text-field :tenant/payment-method tenant]
    [text-field :tenant/customer-success-manager-email tenant]
    [text-field :tenant/account-manager-email tenant]
    [text-field :tenant/first-closer-email tenant]

    [block-password-login tenant]

    [tenant-images tenant]

    [esign-v2 tenant]

    [nev-2023-selector tenant]]])

(defn sso-settings-view
  [tenant]
  (let [features (-> tenant
                     :tenant/product-plan
                     :features)
        feature-on? (or (-> features
                            :feature/saml-sso
                            :status
                            (= :in-use))
                        (-> features
                            :feature/azure-sso
                            :status
                            (= :in-use)))
        custom-login-path (-> tenant
                              :tenant/custom-login-path)
        validate-login-path (fn [new-value]
                              (when-not (re-matches #"^[a-zA-Z0-9-]*" (str new-value))
                                (loc/tr :custom-login-path-must-be-valid-path)))]
    [:div
     {:class "pb-4 flex flex-col gap-3"}
     [:div
      {:class "font-bold text-xl"}
      (loc/tr :sso)
      " (feature "
      (if feature-on?
        "on"
        "not on")
      ")"]
     (when feature-on?
       [validated-text-field
        {:label     (loc/tr :custom-login-path-legacy)
         :value     custom-login-path
         :validate! validate-login-path
         :save!     (fn [new-value]
                      (events/modify-login-path tenant new-value))}])

     [:div
      [labelled-input/view
       {:label     (loc/tr :tenant/max-number-of-login-page-configs)
        :value     (login-page-config-schema/get-max-number-of-login-page-configs tenant)
        :data-type :data.type/integer
        :on-blur   (fn [value]
                     (when (int? value)
                       (events/modify-tenant
                        tenant
                        :tenant/max-number-of-login-page-configs
                        value)))}]]]))

(defn validate-domain
  [value]
  (when-not (re-matches #"^[a-zA-Z0-9-.]*" (str value))
    (loc/tr :reporting-domain-must-be-valid-domain-name)))

(defn validate-email
  [value]
  (when (and (not (cu/blank-value? value))
             (not (email/valid-email-pattern? value)))
    (loc/tr :reporting-email-must-be-valid-email)))

(defn lp-reporting-settings-view
  [tenant]
  (let [features (-> tenant
                     :tenant/product-plan
                     :features)
        feature-on? (some-> features
                            :feature/lp-reporting
                            :status
                            (= :in-use))
        portal-domain (:tenant/reporting-domain tenant)
        portal-theme (:tenant/reporting-theme tenant)
        portal-email-sender (:tenant/lp-reporting-sender-email tenant)
        portal-email-contact (:tenant/reporting-contact-email tenant)]
    [:div
     {:class "flex flex-col gap-3"}
     [:div
      {:class "font-bold text-xl"}
      (loc/tr :feature/lp-reporting)
      " (feature " (when-not feature-on? "not ") "on)"]
     (when feature-on?
       [:<>
        [validated-text-field
         {:label     (loc/field-name :tenant/reporting-domain nil)
          :value     portal-domain
          :validate! validate-domain
          :save!     (fn [new-value]
                       (events/modify-reporting-domain tenant new-value))}]
        [validated-text-field
         {:label     (loc/field-name :tenant/lp-reporting-sender-email nil)
          :value     portal-email-sender
          :validate! validate-email
          :save!     (fn [new-value]
                       (events/modify-reporting-sender-email tenant new-value))}]
        [validated-text-field
         {:label     (loc/field-name :tenant/reporting-contact-email nil)
          :value     portal-email-contact
          :validate! validate-email
          :save!     (fn [new-value]
                       (events/modify-reporting-contact-email tenant new-value))}]
        [labelled-input/view
         {:editor-type   :editor.type/select
          :data-type     :data.type/keyword
          :label         (loc/field-name :tenant/reporting-theme nil)
          :is-clearable? false
          :value         portal-theme
          :options       (->> theme-registry/theme-options
                              (mapv (fn [[k v]]
                                      {:value k
                                       :label v})))
          :on-change     (fn [new-value & _]
                           (when (not= new-value portal-theme)
                             (events/modify-tenant tenant :tenant/reporting-theme new-value)))}]
        [:div
         {:class "flex flex-col gap-2"}
         [:label
          {:class "text-semibold"}
          (loc/tr :tenant/portal-background-image)]
         [tenant-image tenant :tenant/portal-background-image (loc/tr :tenant/portal-background-image)]]
        [:div
         {:class "flex flex-col gap-2"}
         [:label
          {:class "text-semibold"}
          (loc/tr :tenant/reporting-logo-white)]
         [tenant-image tenant :tenant/reporting-logo-white (loc/tr :tenant/reporting-logo-white)]]
        [:div
         {:class "flex flex-col gap-2"}
         [:label
          {:class "text-semibold"}
          (loc/tr :tenant/reporting-logo-grey)]
         [tenant-image tenant :tenant/reporting-logo-grey (loc/tr :tenant/reporting-logo-grey)]]
        [labelled-input/view
         {:editor-type :editor.type/multi-select
          :data-type   :data.type/keyword-multi
          :label       (loc/field-name :tenant/reporting-fund-templates nil)
          :class       "flex-wrap h-auto"
          :value       (:tenant/reporting-fund-templates tenant)
          :options     (->> lp-report/hardcoded-fund-templates
                            (mapv (fn [[k v]]
                                    {:value k
                                     :label v})))
          :on-change   (fn [new-value & _]
                         (when (not= (set new-value) (:tenant/reporting-fund-templates tenant))
                           (events/modify-tenant tenant
                                                 :tenant/reporting-fund-templates
                                                 (set new-value))))}]
        [labelled-input/view
         {:editor-type :editor.type/multi-select
          :data-type   :data.type/keyword-multi
          :label       (loc/field-name :tenant/reporting-holding-templates nil)
          :class       "flex-wrap h-auto"
          :value       (:tenant/reporting-holding-templates tenant)
          :options     (->> lp-report-holding/hardcoded-holding-templates
                            (mapv (fn [[k v]]
                                    {:value k
                                     :label v})))
          :on-change   (fn [new-value & _]
                         (when (not= (set new-value) (:tenant/reporting-holding-templates tenant))
                           (events/modify-tenant tenant
                                                 :tenant/reporting-holding-templates
                                                 (set new-value))))}]])]))

(defn metabase-config
  [_md _index _tenant]
  (let [temp-state (r/atom {})]
    (fn [md index tenant]
      [:li
       {:class "pt-3 flex gap-3 flex-row align-items-center justify-items-center"}
       [data-input/view
        {:data-type :data.type/text-simple
         :value     (str (:db/id md))
         :disabled? true}]
       [data-input/view
        {:data-type :data.type/text-simple
         :value     (or (:metabase-dashboard/label @temp-state)
                        (:metabase-dashboard/label md))
         :on-change (fn [v]
                      (swap! temp-state assoc :metabase-dashboard/label v))
         :on-blur   (fn [_v]
                      (let [modified-value (:metabase-dashboard/label @temp-state)]
                        (when (and (not (cu/blank-value? modified-value))
                                   (not= modified-value (:metabase-dashboard/label md)))

                          (entity-actions/*->field-update!
                           (:db/id md) :metabase-dashboard/label modified-value
                           #(swap! temp-state dissoc :metabase-dashboard/label)
                           #(js/console.log "Error updating metabase " %)))))}]
       [data-input/view
        {:data-type :data.type/text
         :value     (or (:metabase-dashboard/url @temp-state)
                        (:metabase-dashboard/url md))
         :on-change (fn [v]
                      (swap! temp-state assoc :metabase-dashboard/url v))
         :on-blur   (fn [_v]
                      (let [modified-value (:metabase-dashboard/url @temp-state)]
                        (when (and (not (cu/blank-value? modified-value))
                                   (not= modified-value (:metabase-dashboard/url md)))
                          (entity-actions/*->field-update!
                           (:db/id md) :metabase-dashboard/url modified-value
                           #(swap! temp-state dissoc :metabase-dashboard/url)
                           #(js/console.log "Error updating metabase " %)))))}]
       [data-input/view
        {:data-type :data.type/integer
         :class     "w-16"
         :value     (or (:metabase-dashboard/metabase-dashboard-id @temp-state)
                        (:metabase-dashboard/metabase-dashboard-id md))
         :on-change (fn [v]
                      (swap! temp-state assoc :metabase-dashboard/metabase-dashboard-id v))
         :on-blur   (fn [_v]
                      (let [modified-value (:metabase-dashboard/metabase-dashboard-id @temp-state)]
                        (when (and (not (cu/blank-value? modified-value))
                                   (not= modified-value (:metabase-dashboard/metabase-dashboard-id md)))
                          (entity-actions/*->field-update!
                           (:db/id md) :metabase-dashboard/metabase-dashboard-id modified-value
                           #(swap! temp-state dissoc :metabase-dashboard/metabase-dashboard-id)
                           #(js/console.log "Error updating metabase " %)))))}]
       (let [temp-setting (:metabase-dashboard/pipeline-filtering? @temp-state)]
         [labelled-input/view
          {:label       (loc/tr :metabase-dashboard/pipeline-filtering?)
           :horizontal? true
           :editor-type :editor.type/checkbox
           :value       (if (boolean? temp-setting)
                          temp-setting
                          (:metabase-dashboard/pipeline-filtering? md))
           :on-change   (fn [v]
                          (swap! temp-state assoc :metabase-dashboard/pipeline-filtering? v))
           :on-blur     (fn [_v]
                          (let [modified-value (:metabase-dashboard/pipeline-filtering? @temp-state)]
                            (when (and (not (cu/blank-value? modified-value))
                                       (not= modified-value (:metabase-dashboard/pipeline-filtering? md)))
                              (entity-actions/*->field-update!
                               (:db/id md) :metabase-dashboard/pipeline-filtering? modified-value
                               #(swap! temp-state dissoc :metabase-dashboard/pipeline-filtering?)
                               #(js/console.log "Error updating metabase " %)))))}])
       [button/square-secondary-open
        {:on-click (fn [_]
                     (entity-actions/*->disable! (:db/id md)
                                                 js/console.log
                                                 js/console.error))}
        [:i.fal.fa-trash-alt]]
       (when (pos? index)
         [button/secondary-hollow
          {:on-click (fn [_]
                       (events/reorder tenant index (dec index)))}
          [:i.fal.fa-arrow-up]])])))

(defn metabase-report-settings-view
  [tenant]
  (let [features (-> tenant
                     :tenant/product-plan
                     :features)
        feature-on? (some-> features
                            :feature/metabase-dashboards
                            :status
                            (= :in-use))]
    [:div.m-3
     [:div.title-h4
      (loc/tr :feature/metabase-dashboards)
      " (feature " (when-not feature-on? "not ") "on)"]
     [:ul
      {:class "flex flex-col pb-3 gap-2"}
      (->> @sys-admin-db/zadmin-metabase-reports
           (map-indexed (fn [index md]
                          ^{:key (str "metabase-dashboard-config-" (:db/id md) "-" (:common/ord md))}
                          [metabase-config md index tenant]))
           (doall))]
     [button/secondary-hollow
      {:on-click (fn [_]
                   (events/create-metabase-dashboard tenant))}
      (loc/tr :create-metabase-dashboard)]]))

(defn zap-vault-settings-view
  [tenant]
  (let [features (-> tenant
                     :tenant/product-plan
                     :features)
        feature-on? (some-> features
                            :feature/zap-vault
                            :status
                            (= :in-use))]
    [:div.m-3
     [:div.title-h4
      (str (loc/tr :feature/zap-vault) " (feature " (when-not feature-on? "not ") "on)")]
     [labelled-input/view
      {:label     (loc/tr :tenant/vault-domain)
       :data-type :data.type/text-simple
       :value     (:tenant/vault-domain tenant)
       :on-blur   (fn [v]
                    (let [trimmed (cu/str-trim-lcase v)]
                      (when (not= trimmed (:tenant/vault-domain tenant))
                        (events/modify-tenant tenant
                                              :tenant/vault-domain
                                              trimmed))))}]]))

(defn feature-settings-view
  [tenant]
  [:div
   {:class "border border-solid border-base-light-border-paler rounded"}
   [:div
    {:class "p-3 flex justify-between items-center border-b border-solid border-base-light-border-paler"}
    [:h2
     {:class "font-bold"}
     (loc/tr :feature-settings)]]
   [:div
    {:class "p-3"}
    [sso-settings-view tenant]
    [lp-reporting-settings-view tenant]
    [metabase-report-settings-view tenant]
    [zap-vault-settings-view tenant]]])

(defn features
  []
  [:feature/fundraising
   :feature/dealflow
   :feature/sales-pipelines
   :feature/exit-process-management
   :feature/team-management
   :feature/read-only-access
   :feature/read-only-teams
   :feature/fund-management
   :feature/asset-types-company
   :feature/asset-types-real-estate
   :feature/asset-types-other
   :feature/asset-types-fof
   :feature/kyc
   :feature/tenant-kyc
   :feature/surveys
   :feature/survey-multi-responses
   :feature/survey-skip-responses
   :feature/survey-sms-auth
   :feature/survey-excel-import
   :feature/survey-mass-sending
   :feature/indirect-holdings
   :feature/editable-grid
   :feature/risk-module
   :feature/liabilities
   :feature/pdf-memo
   :feature/fee-tracking
   :feature/value-creation-activity
   :feature/application-analytics
   :feature/tasks-kanban
   :feature/screening
   :feature/insider-groups
   :feature/metabase-dashboards
   :feature/old-dashboards
   :feature/reporting-db
   :feature/pre-investment-checks
   :feature/entity-esign
   :feature/entity-graph
   :feature/create-config
   :feature/new-froppo-creation
   :feature/stage-entry-criteria
   :feature/zap-vault
   :feature/decisions
   :feature/docusign])

(def add-ons
  [:feature/nylas-integration
   :feature/enrichment-clearbit
   :feature/enrichment-cruchbase
   :feature/enrichment-vainu
   :feature/authentication-2fa
   :feature/kpi-reporting
   :feature/saml-sso
   :feature/azure-sso
   :feature/lp-reporting])

(defn experimental-features
  []
  [:feature/customizable-nev
   :feature/new-nev
   :feature/nev-v3
   :feature/new-grid-2023
   :feature/metabase-reporting-db
   :feature/stop-sync-in-background
   :feature/performance-testing
   :feature/limited-data-sync
   :feature/counterparty-diligence
   :feature/automated-kyc-renewal
   :feature/contract-kyc
   :feature/new-sso-2025
   :feature/outlook-addin
   :feature/ai-deal-parsing
   :feature/transaction-process
   :feature/transaction-process-dev-mode
   :feature/mcp-connector])

(defmulti feature-config
  :feature-key)

(defmethod feature-config :feature/enrichment-clearbit
  [_]
  {:tooltip "Remember to define feature specific settings below"})

(defmethod feature-config :feature/stage-entry-criteria
  [_]
  {:tooltip (str "Relies on 'Custom create config' and "
                 "'New opportunity creation flow' — those should be "
                 "toggled on for this feature.")})

(defmethod feature-config :default
  [_]
  nil)

(defn features-view
  [tenant current-plan changes]
  [:div
   {:class "border border-solid border-base-light-border-paler rounded"}
   [:div
    {:class "p-3 flex justify-between items-center border-b border-solid border-base-light-border-paler"}
    [:h2
     {:class "font-bold"}
     (loc/tr :features)]
    [button/sm-primary-solid
     {:disabled? (and (nil? (first changes))
                      (nil? (second changes)))
      :on-click  #(events/submit tenant sys-admin-db/modified-plan)}
     [:i.fal.fa-save]
     [:span (loc/tr :save)]]]
   [:div
    {:class "w-full overflow-x-auto"}
    [:div
     {:class "w-full p-3"}
     [:div
      {:class "p-3 flex justify-between items-center bg-base-regular font-bold"}
      [:div (loc/tr :feature/name)]
      [:div {:class "text-right"}
       (loc/tr :feature/status)]]
     [:div
      {:class "p-3 flex flex-col gap-2"}
      [feature/plan-view tenant current-plan]
      (doall
       (for [f (features)]
         ^{:key (str "table-feature-item-" (name f))}
         [feature/view
          {:tenant         tenant
           :modified-state sys-admin-db/modified-features
           :feature-key    f
           :config         (feature-config {:tenant      tenant
                                            :feature-key f})}]))
      [:div
       {:class "py-2 flex justify-center"}
       [:h2
        {:class "font-bold text-lg"}
        "Add Ons"]]
      (doall
       (for [f add-ons]
         ^{:key (str "table-feature-item-" (name f))}
         [feature/view
          {:tenant         tenant
           :modified-state sys-admin-db/modified-features
           :feature-key    f
           :config         (feature-config {:tenant      tenant
                                            :feature-key f})}]))
      [:div
       {:class "py-2 flex justify-center"}
       [:h2
        {:class "font-bold text-lg"}
        "Experimental - INTERNAL USE ONLY"]]
      (doall
       (for [f (experimental-features)]
         ^{:key (str "table-feature-item-" (name f))}
         [feature/view
          {:tenant         tenant
           :modified-state sys-admin-db/modified-features
           :feature-key    f
           :config         (feature-config {:tenant      tenant
                                            :feature-key f})}]))]]]])

(defn toolbar-buttons
  [tenant-id state]
  (let [reloading? (:reloading @state)]
    [{:toolbar-button?  true
      :dropdown-action? true
      :ord              1
      :icon             (if reloading? "fa-sync fa-pulse" :fa-sync)
      :label            (if reloading? (loc/tr :reloading) (loc/tr :refresh))
      :primary-button?  true
      :on-select        (fn [_]
                          (swap! state assoc :reloading true)
                          (conn/send! :report/recalculate-tenant-reports
                                      {:tenant-id tenant-id}
                                      (fn [_resp]
                                        (swap! state assoc :reloading false))
                                      (* 1000 60 5)))}]))

(defn reset-caches-button
  [_tenant]
  (let [view-state (r/atom {})]
    (fn [tenant]
      (let [resetting-caches? (:resetting-caches? @view-state)]
        [button/sm-warning-solid
         (merge
          {:on-click (fn [e]
                       (fu/prevent-default-event! e)
                       (when-not resetting-caches?
                         (modals/open-modal!
                          confirmation/modal-key
                          {:body         [:div (loc/tr :reset-browser-caches-info)]
                           :confirm-text (loc/tr :do-it)
                           :on-confirm   (fn [_]
                                           (events/reset-client-caches view-state tenant))})))}
          (when resetting-caches?
            {:disabled? true}))
         (loc/tr :reset-client-caches)]))))

(defn marked-for-deletion-toggle
  [tenant]
  [:div
   {:class "flex items-center gap-2"}
   [:label (loc/tr :tenant/marked-for-excision)]
   [toggle/toggle
    {:checked?  (boolean (:tenant/marked-for-excision tenant))
     :on-change (fn [toggled?]
                  (rf/dispatch [::event/toggle-marked-for-excision (:db/id tenant) toggled?]))}]])
(defn suspended-toggle
  [tenant]
  [:div
   {:class "flex items-center gap-2"}
   [:label (loc/tr :tenant/suspended)]
   [toggle/toggle
    {:checked?  (boolean (:tenant/suspended tenant))
     :on-change (fn [toggled?]
                  (rf/dispatch [::event/toggle-suspended (:db/id tenant) toggled?]))}]])

(defn view
  [tenant-id]
  (r/create-class
   {:component-did-mount
    (fn [_]
      (swap! sys-admin-db/sys-admin assoc
             :modified-plan
             {:features {}})
      (swap! sys-admin-db/tenant assoc :reports [])
      (subscription/open ::zadmin-tenant
                         :zadmin-tenant
                         {:tenant-id tenant-id}
                         (fn [{:keys [entities]}]
                           (fu/manage-entities session/reverse-lookup
                                               sys-admin-db/tenant-data
                                               entities
                                               :keep-disabled
                                               [:entity.type/tenant])
                           :ok)))
    :component-will-unmount
    (fn [_]
      (reset! sys-admin-db/tenant-data {})
      (subscription/close ::zadmin-tenant))
    :reagent-render
    (fn [tenant-id]
      (let [{:keys [entities]} @sys-admin-db/tenant-data
            tenant (get entities tenant-id)
            current-plan (:tenant/product-plan tenant)
            changes (data/diff {:features {}} @sys-admin-db/modified-plan)]
        (when (and (some? tenant)
                   (or (session/sys-admin?)
                       (session/zadmin?)))
          [:div
           {:class "p-4 container max-w-[1024px] flex flex-col gap-3"}
           [:h1
            {:class "flex items-center gap-3 text-xl"}
            (:tenant/name tenant)
            [:div
             {:class "text-base-light-text-paler"}
             (loc/tr :settings-tenant-id [tenant-id])]]
           [:div
            {:class "flex w-full justify-between flex-1"}
            [reset-caches-button tenant]
            [suspended-toggle tenant]
            [marked-for-deletion-toggle tenant]]
           [configuration-view tenant]
           [holding-creation tenant]
           [features-view tenant current-plan changes]
           [feature-settings-view tenant current-plan changes]
           [zadmin-analytics/view tenant]
           [tenant-users/view tenant]
           [tenant-sessions/render tenant]])))}))
